import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { hasValue } from 'Lib/Any';
import { convertLabelMapToConfig, convertLabelUomMapToConfig, mergeConfig } from '../../../Core/Helpers/configHelpers';

const getCwdChartXAxisLabel = (worksheet: any) =>
  hasValue(worksheet?.data?.Preparation?.FineMoistureContent) ? 'Estimated Moisture Content' : 'Water Added';

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.Preparation.StabiliserUsed`]: `Stabilized Material`,
      [`${prefix}.Preparation.StabiliserAddedAtSampling`]: `Stabilizer Added`,
      [`${prefix}.Preparation.StabiliserAddedAtDateTimeLocal`]: `Date and Time Stabilizer Added`,
      [`${prefix}.Preparation.FineMoistureContentDetermined`]: `Test Portion Moisture Content Required`,

      [`${prefix}.Curing.SandOrGranularMaterial`]: 'Sand or Granular Material',
      [`${prefix}.Curing.ForCompactionControl`]: 'For Compaction Control (not for Assigned Value)',
      [`${prefix}.Curing.LiquidLimitDetermination`]: 'Liquid Limit Determination',
      [`${prefix}.Curing.LiquidLimit`]: 'Liquid Limit',
      [`${prefix}.Curing.LiquidLimitEstimatedCategory`]: 'Liquid Limit',
      [`${prefix}.Curing.LiquidLimitSourceRecord`]: 'Source Record ID',

      [`${prefix}.Curing.AssociatedLiquidLimit`]: 'Liquid Limit Result',
      [`${prefix}.Curing.AssociatedLiquidLimit.ImportedTest.Results.LiquidLimit`]: 'Liquid Limit',
      [`${prefix}.Curing.AssociatedLiquidLimit.ExternalTest.Results.LiquidLimit`]: 'Liquid Limit',

      [`${prefix}.Curing.CuringAppliesToSubSamples`]: 'Use the same curing conditions for all sub-samples',
      [`${prefix}.Curing.PortionCuring.StartedAtDateTime`]: 'Curing Started',
      [`${prefix}.Curing.PortionCuring.CompletedAtDateTime`]: 'Curing Finished',
      [`${prefix}.Curing.PortionCuring.Duration`]: 'Curing Time',
      [`${prefix}.Curing.PortionCuring.TimeBetweenAdditionOfStabiliserAndCompaction`]: 'Addition of Stabilizer to Compaction',

      [`${prefix}.TestFractionParticleDensity.AssociatedTestFractionParticleDensity.ExternalTest.Results.ParticleDensity`]: 'Particle Density',
      [`${prefix}.TestFractionParticleDensity.ParticleDensity`]: 'Test Fraction Particle Density',
      [`${prefix}.TestFractionParticleDensity.ParticleDensityDeterminationMethod`]: 'Determination Method',

      [`${prefix}.Preparation.FineMoistureContent`]: `Test Portion Moisture Content`,
      [`${prefix}.Preparation.FineGrainSize`]: `Test Portion Grain Size`,
      [`${prefix}.Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent`]: 'Moisture Content',
      [`${prefix}.Preparation.EnterConvertedWetDensity`]: 'Enter converted wet density data',

      [`${prefix}.Equipment.Balance`]: 'Balance',
      [`${prefix}.Equipment.CompactionBalance`]: 'Compaction Balance',
      [`${prefix}.Equipment.StraightEdge`]: 'Straight Edge',
      [`${prefix}.Equipment.Mould`]: 'Mould',
      [`${prefix}.Equipment.MouldMass`]: 'Mould Mass',
      [`${prefix}.Equipment.MouldVolume`]: 'Mould Volume',
      [`${prefix}.Equipment.UseMouldForAllTestPortions`]: 'Use for all test portions',
      [`${prefix}.Equipment.Hammer`]: 'Hammer',
      [`${prefix}.Equipment.HammerOperation`]: 'Hammer Operation',
      [`${prefix}.Equipment.HammerFaceType`]: 'Hammer Face Type',
      [`${prefix}.Equipment.HammerFaceDiameter`]: 'Hammer Face Nominal Diameter',
      [`${prefix}.Equipment.HammerFaceRadius`]: 'Hammer Face Nominal Radius',

      [`${prefix}.TestPortions.Mould`]: 'Mould',
      [`${prefix}.TestPortions.MouldMass`]: 'Mould Mass',
      [`${prefix}.TestPortions.MouldVolume`]: 'Mould Volume',
      [`${prefix}.TestPortions.PortionMass`]: 'Portion Mass',
      [`${prefix}.TestPortions.WaterAddedTargetPercent`]: 'Water Added Target',
      [`${prefix}.TestPortions.WaterAddedTarget`]: 'Water to Add',
      [`${prefix}.TestPortions.PortionMassAfterWaterAdded`]: 'Portion Mass After Addition',
      [`${prefix}.TestPortions.WaterAdded`]: 'Water Added',
      [`${prefix}.TestPortions.WaterAddedPercent`]: 'Actual Water Added',
      [`${prefix}.TestPortions.Curing.StartedAtDateTime`]: 'Curing Started',
      [`${prefix}.TestPortions.Curing.CompletedAtDateTime`]: 'Curing Finished',
      [`${prefix}.TestPortions.Curing.Duration`]: 'Curing Time',
      [`${prefix}.TestPortions.Curing.TimeBetweenAdditionOfStabiliserAndCompaction`]: 'Time Between Addition of Stabilizer and Compaction',
      [`${prefix}.TestPortions.WetSoilAndMouldMass`]: 'Wet Soil and Mould Mass',
      [`${prefix}.TestPortions.WetSoilMass`]: 'Wet Soil Mass',
      [`${prefix}.TestPortions.WetDensity`]: 'Wet Density',

      [`${prefix}.Results.MaximumDryDensity`]: 'Maximum Dry Density',
      [`${prefix}.Results.OptimumMoistureContent`]: 'Optimum Moisture Content',

      [`${prefix}.TestPortions.DryDensity`]: 'Dry Density',
      [`${prefix}.TestPortions.ExcludedPoint`]: 'Excluded Point',

      [`${prefix}.Results.MddCurveFittingAlgorithm`]: 'Curve Fitting',

      [`mddChart.XAxisMin`]: 'Moisture Content Start',
      [`mddChart.XAxisMajorStep`]: 'Moisture Content Division Size',
      [`mddChart.YAxisToXAxisStepRatio`]: 'Dry Density to Moisture Content Ratio',

      [`cwdChart.XAxisMin`]: (args: IConfigFunctionArgs) => `${getCwdChartXAxisLabel(args.worksheet)} Start`,
      [`cwdChart.XAxisMajorStep`]: (args: IConfigFunctionArgs) =>
        `${getCwdChartXAxisLabel(args.worksheet)} Division Size`,
      [`cwdChart.YAxisToXAxisStepRatio`]: (args: IConfigFunctionArgs) =>
        `Converted Wet Density to ${getCwdChartXAxisLabel(args.worksheet)} Ratio`,
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.Preparation.FineMoistureContent`]: '%',
      [`${prefix}.Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent`]: '%',

      [`${prefix}.TestPortions.WaterAddedTargetPercent`]: '%',
      [`${prefix}.TestPortions.WaterAddedPercent`]: '%',

      [`${prefix}.Curing.LiquidLimit`]: '%',
      [`${prefix}.Curing.AssociatedLiquidLimit.ImportedTest.Results.LiquidLimit`]: '%',
      [`${prefix}.Curing.AssociatedLiquidLimit.ExternalTest.Results.LiquidLimit`]: '%',

      [`${prefix}.Results.MoistureContentPercent`]: '%',
      [`${prefix}.Results.OptimumMoistureContent`]: '%',

      [`cwdChart.XAxisMin`]: '%',
      [`cwdChart.XAxisMajorStep`]: '%',

      [`mddChart.XAxisMin`]: '%',
      [`mddChart.XAxisMajorStep`]: '%',
    })
  );

export default getLabelConfig;
